/**
 * Categorías unificadas para Posts y Servicios
 * Archivo centralizado para evitar duplicación
 */

export const CATEGORIES = [
  { id: 'all', name: 'Todas' },
  { id: 'digital-art', name: 'Arte Digital' },
  { id: 'illustration', name: 'Ilustración' },
  { id: 'anime-manga', name: 'Anime & Manga' },
  { id: 'comic-manga', name: 'Cómic y Manga' },
  { id: 'portrait', name: 'Retratos' },
  { id: 'game-art', name: 'Arte para Videojuegos' },
  { id: 'design', name: 'Diseño' },
  { id: 'streaming', name: 'Streaming & Contenido' },
  { id: 'vtuber', name: 'VTuber' },
  { id: 'concept', name: 'Concept Art' },
  { id: 'pixel-art', name: 'Pixel Art' },
  { id: 'character-design', name: 'Character Design' },
  { id: 'landscape', name: 'Paisajes' },
  { id: 'fanart', name: 'Fanart' },
  { id: 'nft', name: 'NFT / Crypto Art' },
  { id: 'tattoo', name: 'Tattoo / Tatuajes' },
  { id: 'stickers', name: 'Stickers' },
  { id: 'emotes', name: 'Emotes / Twitch' },
  { id: 'logos-branding', name: 'Logos & Branding' },
  { id: 'ui-ux', name: 'UI/UX Design' },
  { id: 'motion-graphics', name: 'Motion Graphics' },
  { id: 'chibi', name: 'Chibi' },
  { id: '3d', name: '3D' },
  { id: 'photography', name: 'Fotografía' },
  { id: 'specialized', name: 'Especializado' },
  { id: 'general', name: 'General' }
];

/**
 * Categorías para servicios (sin "Todas")
 * Usadas en CreateServiceModal, EditServiceModal
 */
export const SERVICE_CATEGORIES = CATEGORIES.filter(cat => cat.id !== 'all');

/**
 * Obtener categoría por ID
 * @param {string} categoryId - ID de la categoría
 * @returns {Object|null} Objeto de categoría o null si no existe
 */
export const getCategoryById = (categoryId) => {
  return CATEGORIES.find(cat => cat.id === categoryId) || null;
};

/**
 * Obtener nombre de categoría por ID
 * @param {string} categoryId - ID de la categoría
 * @returns {string} Nombre de la categoría o 'Sin categoría'
 */
export const getCategoryName = (categoryId) => {
  const category = getCategoryById(categoryId);
  return category ? category.name : 'Sin categoría';
};

/**
 * Validar si una categoría existe
 * @param {string} categoryId - ID de la categoría
 * @returns {boolean}
 */
export const isValidCategory = (categoryId) => {
  return SERVICE_CATEGORIES.some(cat => cat.id === categoryId);
};

/**
 * Normaliza un valor de categoría (id o nombre viejo) a un categoryId válido.
 * - Si ya es un id válido, lo devuelve.
 * - Si es un nombre (por ej. "Arte Digital"), intenta mapearlo al id correcto.
 * - Si no puede, devuelve null.
 */
export const normalizeCategoryId = (input) => {
  if (!input || typeof input !== 'string') return null;

  const raw = input.trim();
  if (!raw) return null;

  // Si ya es un id válido (incluye 'general', etc.)
  if (CATEGORIES.some((c) => c.id === raw)) return raw;

  const normalizeText = (s) =>
    s
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // quitar tildes
      .replace(/&/g, 'y')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const n = normalizeText(raw);
  if (!n) return null;

  // Match por nombre actual
  const byName = CATEGORIES.find((c) => normalizeText(c.name) === n);
  if (byName) return byName.id;

  // Aliases comunes / nombres viejos
  const aliasMap = {
    'arte digital': 'digital-art',
    'ilustracion': 'illustration',
    'anime manga': 'anime-manga',
    'comic manga': 'comic-manga',
    'comics manga': 'comic-manga',
    'retratos': 'portrait',
    'arte para videojuegos': 'game-art',
    'videojuegos': 'game-art',
    'diseno': 'design',
    'streaming contenido': 'streaming',
    'vtuber': 'vtuber',
    'concept art': 'concept',
    'pixel art': 'pixel-art',
    'character design': 'character-design',
    'paisajes': 'landscape',
    'fanart': 'fanart',
    'nft crypto art': 'nft',
    'crypto art': 'nft',
    'tattoo tatuajes': 'tattoo',
    'tatuajes': 'tattoo',
    'stickers': 'stickers',
    'emotes twitch': 'emotes',
    'logos branding': 'logos-branding',
    'ui ux design': 'ui-ux',
    'motion graphics': 'motion-graphics',
    'chibi': 'chibi',
    '3d': '3d',
    'fotografia': 'photography',
    'especializado': 'specialized',
    'general': 'general',
    'todas': 'all'
  };

  if (aliasMap[n]) return aliasMap[n];

  // Heurística: si contiene palabras clave
  if (n.includes('pixel')) return 'pixel-art';
  if (n.includes('anime')) return 'anime-manga';
  if (n.includes('comic')) return 'comic-manga';
  if (n.includes('retrato')) return 'portrait';
  if (n.includes('paisaj')) return 'landscape';
  if (n.includes('fanart')) return 'fanart';
  if (n.includes('nft') || n.includes('crypto')) return 'nft';
  if (n.includes('tattoo') || n.includes('tatuaj')) return 'tattoo';
  if (n.includes('sticker')) return 'stickers';
  if (n.includes('emote') || n.includes('twitch')) return 'emotes';
  if (n.includes('logo') || n.includes('branding')) return 'logos-branding';
  if (n.includes('ui') || n.includes('ux')) return 'ui-ux';
  if (n.includes('motion') || n.includes('animacion')) return 'motion-graphics';

  return null;
};
